import type {
  CommonResponseListStudentAchievementResponse,
  CommonResponseListStudentAchievementScoredItem,
  CommonResponseSetStudentAchievementLogResponse,
  GetGroupStudentAchievementsByParams,
  GetRecentlyScoredQueueByStudentIdAndChipIdParams,
  GetRecentlyScoredQueuesByParams,
  GetStudentAchievementByStudentIdAndCurriculumKey1Params,
  GetStudentAchievementByStudentIdAndCurriculumKeyParams,
  GetStudentAchievementLogByStudentIdAndCurriculumKeyAndRequestDateParams,
  GetStudentAchievementLogsByParams,
  GroupStudentAchievementByConceptChipResponse,
  GroupStudentAchievementConceptChipResponse,
  GroupStudentAchievementLogConceptChipResponse,
  StudentAchievementAggregateResponse,
} from '~/api-spec/__generated__/main'
import { httpClient, type RequestMeta } from '~/http/client'

import {
  adaptConceptChipScoredItems,
  adaptGroupConceptChipAchievementLogs,
  adaptGroupConceptChipAchievements,
  adaptGroupConceptChipDetail,
  adaptStudentConceptChipAchievementLogs,
  adaptStudentConceptChipAchievements,
  adaptStudentsAchievementSummary,
} from './adapters'
import type {
  ConceptChipScoredItem,
  GroupConceptChipAchievement,
  GroupConceptChipAchievementLog,
  GroupConceptChipDetail,
  StudentConceptChipAchievement,
  StudentConceptChipAchievementLog,
  StudentsAchievementSummary,
} from './schemas'

export type StudentConceptChipAchievementsParams = Omit<
  GetStudentAchievementByStudentIdAndCurriculumKey1Params,
  'studentId'
>
export type StudentConceptChipAchievementLogsParams = Omit<
  GetStudentAchievementLogByStudentIdAndCurriculumKeyAndRequestDateParams,
  'studentId'
>
export type ConceptChipScoredItemsParams = Omit<
  GetRecentlyScoredQueueByStudentIdAndChipIdParams,
  'studentId' | 'conceptChipId'
>
// 서버는 학생 id 집합을 본문으로, curriculumKey 를 쿼리로 받는다. 생성 타입은 둘을 한 본문 객체로 그렸다.
// 2026-10-13 백엔드 develop(cc5f60e17) 의 studentHistory/controller/StudentAchievementController.kt 로 확인.
export type StudentsAchievementSummaryParams =
  GetStudentAchievementByStudentIdAndCurriculumKeyParams & {
    curriculumKey: string
  }
export type GroupConceptChipAchievementsParams = GetGroupStudentAchievementsByParams
export type GroupConceptChipAchievementLogsParams = GetStudentAchievementLogsByParams
export type GroupConceptChipDetailParams = GetRecentlyScoredQueuesByParams

/** 학생 한 명의 유형칩별 성취도 — 채점 기록이 있는 칩만 온다 */
export const getStudentConceptChipAchievements = async (
  studentId: StudentConceptChipAchievement['studentId'],
  params: StudentConceptChipAchievementsParams,
  meta?: RequestMeta,
): Promise<StudentConceptChipAchievement[]> => {
  const body = await httpClient().get<CommonResponseListStudentAchievementResponse>(
    `/student-history/achievement/student/${studentId}`,
    { params, meta },
  )
  return adaptStudentConceptChipAchievements(body.data) ?? []
}

/** 학생 한 명의 유형칩별 성취도 과거 이력 — requestDate 가 속한 주차의 스냅샷이다 */
export const getStudentConceptChipAchievementLogs = async (
  studentId: StudentConceptChipAchievementLog['studentId'],
  params: StudentConceptChipAchievementLogsParams,
  meta?: RequestMeta,
): Promise<StudentConceptChipAchievementLog[]> => {
  const body = await httpClient().get<CommonResponseSetStudentAchievementLogResponse>(
    `/student-history/achievement/student/${studentId}/log`,
    { params, meta },
  )
  return adaptStudentConceptChipAchievementLogs(body.data)
}

/** 학생 한 명이 유형칩 하나에서 최근 채점한 문항들 */
export const getConceptChipScoredItems = async (
  studentId: StudentConceptChipAchievement['studentId'],
  conceptChipId: StudentConceptChipAchievement['conceptChipId'],
  params: ConceptChipScoredItemsParams,
  meta?: RequestMeta,
): Promise<ConceptChipScoredItem[]> => {
  const body = await httpClient().get<CommonResponseListStudentAchievementScoredItem>(
    `/student-history/achievement/student/${studentId}/concept-chips/${conceptChipId}`,
    { params, meta },
  )
  return adaptConceptChipScoredItems(body.data)
}

/** 여러 학생의 유형칩별·학생별 성취도 등급 인원. CommonResponse 봉투 없이 본문을 바로 낸다 */
export const getStudentsAchievementSummary = async (
  studentIds: StudentConceptChipAchievement['studentId'][],
  params: StudentsAchievementSummaryParams,
  meta?: RequestMeta,
): Promise<StudentsAchievementSummary> =>
  adaptStudentsAchievementSummary(
    await httpClient().post<StudentAchievementAggregateResponse>(
      '/student-history/achievement/student',
      studentIds,
      { params, meta },
    ),
  )

/** 그룹 유형칩 성취도 목록 — 학생별 성취도와 평균색을 함께 내준다 */
export const getGroupConceptChipAchievements = async (
  params: GroupConceptChipAchievementsParams,
  meta?: RequestMeta,
): Promise<GroupConceptChipAchievement[]> => {
  const body = await httpClient().get<GroupStudentAchievementConceptChipResponse[]>(
    '/student-history/achievement/by-group/concept-chips',
    { params, meta },
  )
  return adaptGroupConceptChipAchievements(body ?? [])
}

/** 그룹 유형칩 성취도 과거 이력 — 주차 스냅샷이라 학생별 성취도는 없다 */
export const getGroupConceptChipAchievementLogs = async (
  params: GroupConceptChipAchievementLogsParams,
  meta?: RequestMeta,
): Promise<GroupConceptChipAchievementLog[]> => {
  const body = await httpClient().get<GroupStudentAchievementLogConceptChipResponse[]>(
    '/student-history/achievement/by-group/log',
    { params, meta },
  )
  return adaptGroupConceptChipAchievementLogs(body ?? [])
}

/** 그룹 유형칩 상세 — 학생별 성취도와 정답률 상·하위 문제 */
export const getGroupConceptChipDetail = async (
  { conceptChipId, ...params }: GroupConceptChipDetailParams,
  meta?: RequestMeta,
): Promise<GroupConceptChipDetail> => {
  const body = await httpClient().get<GroupStudentAchievementByConceptChipResponse>(
    `/student-history/achievement/by-group/concept-chips/${conceptChipId}`,
    { params, meta },
  )
  return adaptGroupConceptChipDetail(body)
}
